import React, { FC, ReactElement, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import {
  ShoppingOutlined, CloseCircleOutlined, EnvironmentOutlined,
  CreditCardOutlined, CheckCircleOutlined, LockOutlined, RightOutlined,
} from "@ant-design/icons";
import { Form, Typography, Radio, Modal, Spin } from "antd";

import FormInput from "../../components/FormInput/FormInput";
import { selectUserFromUserState } from "../../redux-toolkit/user/user-selector";
import { selectCartItems, selectTotalPrice } from "../../redux-toolkit/cart/cart-selector";
import { resetOrderState, setOrderLoadingState } from "../../redux-toolkit/order/order-slice";
import { LoadingStatus } from "../../types/types";
import { resetCartState } from "../../redux-toolkit/cart/cart-slice";
import { fetchCart } from "../../redux-toolkit/cart/cart-thunks";
import OrderItem from "./OrderItem/OrderItem";
import { BASE_URL } from "../../constants/urlConstants";
import "./Order.css";

type PaymentType    = "COD" | "ONLINE";
type DeliveryStatus = "AVAILABLE" | "NOT_AVAILABLE" | null;

declare global { interface Window { Razorpay?: any; } }

// ✅ Key loaded from env — never hardcoded
const RAZORPAY_KEY = process.env.REACT_APP_RAZORPAY_KEY_ID ?? "";

const showErrorModal = (msg: string) =>
  Modal.error({ title: "Error", content: msg, okText: "OK", centered: true });

const safeFetch = async (
  url: string,
  options?: RequestInit
): Promise<{ ok: boolean; status: number; data: any }> => {
  const res  = await fetch(url, options);
  const text = await res.text();
  let data: any = text;
  try { if (text) data = JSON.parse(text); } catch { }
  return { ok: res.ok, status: res.status, data };
};

const Order: FC = (): ReactElement => {
  const dispatch = useDispatch();
  const history  = useHistory();
  const [form]   = Form.useForm();

  const [orderPlaced,    setOrderPlaced]    = useState(false);
  const [orderId,        setOrderId]        = useState<number | null>(null);
  const [orderError,     setOrderError]     = useState<string | null>(null);
  const [paymentType,    setPaymentType]    = useState<PaymentType>("COD");
  const [deliveryStatus, setDeliveryStatus] = useState<DeliveryStatus>(null);
  const [progress,       setProgress]       = useState({ open: false, text: "" });
  const [minOrderValue,  setMinOrderValue]  = useState<number>(0);
  const [perfumesFromLocalStorage, setPerfumesFromLocalStorage] =
    useState<Map<number, number>>(new Map());

  const usersData  = useSelector(selectUserFromUserState);
  const perfumes   = useSelector(selectCartItems);
  const totalPrice = useSelector(selectTotalPrice);

  const role          = localStorage.getItem("role");
  const isDistributor = role === "3";
  const isRetailer    = role === "2";

  useEffect(() => {
    const fetchMinOrder = async () => {
      try {
        const endpoint = isDistributor
          ? `${BASE_URL}/api/v1/config/dist_max`
          : `${BASE_URL}/api/v1/config/retailer_max`;
        const { ok, data } = await safeFetch(endpoint);
        if (ok) setMinOrderValue(Number(data));
      } catch { }
    };
    fetchMinOrder();
  }, [isRetailer, isDistributor]);

  useEffect(() => {
    const stored = new Map<number, number>(
      JSON.parse(localStorage.getItem("perfumes") || "[]")
    );
    setPerfumesFromLocalStorage(stored);
    dispatch(setOrderLoadingState(LoadingStatus.LOADED));
    dispatch(fetchCart(Array.from(stored.keys())));
    if (usersData) form.setFieldsValue(usersData);
    fetchPrimaryAddress();
    return () => { dispatch(resetOrderState()); dispatch(resetCartState()); };
  }, [dispatch, form, usersData]);

  const fetchPrimaryAddress = async () => {
    const msisdn = localStorage.getItem("mobile");
    console.log("mobile:", localStorage.getItem("mobile"));
    console.log("msisdn:", msisdn);

    if (!msisdn) return;
    try {
      const { ok, data } = await safeFetch(`${BASE_URL}/api/address/get`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ msisdn }),
      });
      if (!ok || !Array.isArray(data) || !data.length) return;
      const addr = data.find((a: any) => a.primary) ?? data[0];
      form.setFieldsValue({
        address: addr.address ?? "", city: addr.city ?? "",
        state: addr.state ?? "", postIndex: addr.pincode ?? "",
      });
      setDeliveryStatus("AVAILABLE");
    } catch { }
  };

  const fetchPincodeDetails = async (pincode: string) => {
    if (!/^\d{6}$/.test(pincode)) return;
    form.setFieldsValue({ city: "", state: "" });
    setDeliveryStatus(null);
    try {
      const { ok, data } = await safeFetch(`${BASE_URL}/api/v1/order/pincode/${pincode}`);
      if (ok && data) {
        form.setFieldsValue({ city: data.city, state: data.state });
        setDeliveryStatus(data.deliveryAvailable ? "AVAILABLE" : "NOT_AVAILABLE");
        return;
      }
    } catch { }
    try {
      const { ok, data } = await safeFetch(`https://api.postalpincode.in/pincode/${pincode}`);
      if (!ok || !data?.[0]?.PostOffice?.length) { setDeliveryStatus("NOT_AVAILABLE"); return; }
      const po = data[0].PostOffice[0];
      form.setFieldsValue({ city: po.District, state: po.State });
      setDeliveryStatus(po.State === "Karnataka" ? "AVAILABLE" : "NOT_AVAILABLE");
    } catch {
      showErrorModal("Failed to fetch pincode details.");
      setDeliveryStatus("NOT_AVAILABLE");
    }
  };

  const loadRazorpay = (): Promise<boolean> => {
    if (window.Razorpay) return Promise.resolve(true);
    return new Promise((resolve) => {
      const s = document.createElement("script");
      s.src     = "https://checkout.razorpay.com/v1/checkout.js";
      s.onload  = () => resolve(true);
      s.onerror = () => resolve(false);
      document.body.appendChild(s);
    });
  };

  const placeOrder = async (transactionId: string | null) => {
    const perfumesId    = Object.fromEntries(
      new Map(JSON.parse(localStorage.getItem("perfumes") || "[]"))
    );
    const distributorId = localStorage.getItem("distributorId");
    const url  = isDistributor ? `${BASE_URL}/api/finalle/dist/order` : `${BASE_URL}/api/v1/order`;
    const body = isDistributor
      ? { distributorId: Number(distributorId), ...form.getFieldsValue(), perfumesId, paymentType, transactionId }
      : { ...form.getFieldsValue(), perfumesId, totalPrice, paymentType, transactionId };

    const { ok, data } = await safeFetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });

    if (!ok) {
      const msg =
        typeof data === "object" && data?.message ? data.message :
        typeof data === "string" && data.trim()   ? data :
        "Order failed. Please try again.";
      throw new Error(msg);
    }

    localStorage.removeItem("perfumes");
    dispatch(resetCartState());
    setOrderId(data?.id ?? null);
    setOrderPlaced(true);
  };

  // ✅ Step 1: verify signature on backend, THEN place order
  // This replaces the old handler that skipped verification entirely
  const handlePaymentSuccess = async (razorpayResponse: {
    razorpay_payment_id: string;
    razorpay_order_id: string;
    razorpay_signature: string;
  }) => {
    setProgress({ open: true, text: "Verifying payment..." });

    const { ok, data } = await safeFetch(`${BASE_URL}/api/payment/verify-payment`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(razorpayResponse),
    });

    if (!ok || !data?.success) {
      // ✅ Signature mismatch — do NOT place order
      throw new Error(
        data?.error ?? "Payment verification failed. Contact support with your payment ID."
      );
    }

    // ✅ Only reach here if backend confirmed the signature
    setProgress({ open: true, text: "Confirming your order..." });
    await placeOrder(razorpayResponse.razorpay_payment_id);
  };

  const isFormValid = () => {
    const v = form.getFieldsValue();
    return !!(v.address && v.postIndex && v.city && v.state && deliveryStatus === "AVAILABLE");
  };

  const onFormSubmit = async () => {
      setOrderError(null);
      try {
        if (minOrderValue > 0 && totalPrice < minOrderValue)
          throw new Error(`Minimum order value is ₹${minOrderValue}`);

        if (paymentType === "ONLINE" && !RAZORPAY_KEY)
          throw new Error("Payment configuration error. Please contact support.");

        setProgress({ open: true, text: "Checking your order..." });

        // ✅ STEP 1 — Validate stock BEFORE any payment
        const perfumesId = Object.fromEntries(
          new Map(JSON.parse(localStorage.getItem("perfumes") || "[]"))
        );
         const msisdn = localStorage.getItem("mobile");

        const { ok: validOk, data: validData } = await safeFetch(
          `${BASE_URL}/api/v1/order/validate`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ phoneNumber: msisdn, perfumesId }),
          }
        );

        if (!validOk || !validData?.valid) {
          // ✅ Stock/product error — shown BEFORE payment modal opens
          throw new Error(validData?.error ?? "Some items are unavailable.");
        }

        // ✅ STEP 2 — COD: place order directly
        if (paymentType === "COD") {
          setProgress({ open: true, text: "Processing your order..." });
          await placeOrder(null);
          return;
        }

        // ✅ STEP 3 — Online: stock confirmed, now collect payment
        const loaded = await loadRazorpay();
        if (!loaded) throw new Error("Failed to load payment gateway. Please check your connection.");

        setProgress({ open: true, text: "Initializing payment..." });

        const { ok: payOk, data: payData } = await safeFetch(
          `${BASE_URL}/api/payment/create-order`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              amount: Math.max(100, Math.round(totalPrice * 100)),
            }),
          }
        );

        if (!payOk || !payData?.id) {
          const msg = typeof payData === "object" && payData?.message
            ? payData.message : "Payment initialization failed. Please try again.";
          throw new Error(msg);
        }

        setProgress({ open: false, text: "" });

        const rzp = new window.Razorpay({
          key:         RAZORPAY_KEY,
          amount:      payData.amount,
          currency:    payData.currency,
          name:        "Finllie",
          description: "Order Payment",
          order_id:    payData.id,

          handler: async (response: {
            razorpay_payment_id: string;
            razorpay_order_id: string;
            razorpay_signature: string;
          }) => {
            try {
              await handlePaymentSuccess(response);
            } catch (err: any) {
              setProgress({ open: false, text: "" });
              setOrderError(err.message);
            }
          },

          modal: {
            ondismiss: () => setProgress({ open: false, text: "" }),
          },

          prefill: {
            name:    `${form.getFieldValue("firstName") ?? ""} ${form.getFieldValue("lastName") ?? ""}`.trim(),
            email:   form.getFieldValue("email")       ?? "",
            contact: form.getFieldValue("phoneNumber") ?? "",
          },

          theme: { color: "#3399cc" },
        });

        rzp.on("payment.failed", (resp: any) => {
          setProgress({ open: false, text: "" });
          showErrorModal(resp?.error?.description || "Payment failed. Please try again.");
        });

        rzp.open();

      } catch (err: any) {
        setOrderError(err.message ?? "Something went wrong.");
      } finally {
        if (paymentType !== "ONLINE") setProgress({ open: false, text: "" });
      }
  };

  const minOrderFail = minOrderValue > 0 && totalPrice < minOrderValue;

  if (orderPlaced) return (
    <div className="ord-success">
      <div className="ord-success-icon"><CheckCircleOutlined /></div>
      <h2 className="ord-success-title">Order Placed Successfully!</h2>
      <p className="ord-success-sub">Order #{orderId} · You'll receive a confirmation shortly</p>
      <button className="ord-success-btn" onClick={() => history.push("/")}>Continue Shopping</button>
    </div>
  );

  if (orderError) return (
    <div className="ord-success">
      <div className="ord-success-icon error"><CloseCircleOutlined /></div>
      <h2 className="ord-success-title">Order Failed</h2>
      <p className="ord-success-sub">{orderError}</p>
      <button className="ord-success-btn" onClick={() => setOrderError(null)}>Try Again</button>
    </div>
  );

  return (
    <>
      <Modal open={progress.open} footer={null} closable={false} centered>
        <div className="ord-progress-inner">
          <Spin size="large" />
          <Typography.Text className="ord-progress-text">{progress.text}</Typography.Text>
        </div>
      </Modal>

      <div className="ord-page">
        <div className="ord-page-header">
          <ShoppingOutlined className="ord-page-header-icon" />
          <span className="ord-page-header-title">Checkout</span>
        </div>

        <div className="ord-layout">
          <div className="ord-left">
            <div className="ord-card">
              <div className="ord-card-header">
                <EnvironmentOutlined className="ord-card-icon" />
                <span className="ord-card-title">Delivery Address</span>
                <button className="ord-manage-addr-btn" onClick={() => history.push("/account/user/myaddress")}>
                  Manage Addresses <RightOutlined style={{ fontSize: 11 }} />
                </button>
              </div>

              <Form form={form} layout="vertical"
                onValuesChange={(_, all) => {
                  if (all.postIndex?.length === 6) fetchPincodeDetails(all.postIndex);
                }}
              >
                {!isDistributor && (
                  <>
                    <div className="ord-form-row">
                      <FormInput title="First Name" name="firstName" titleSpan={8} wrapperSpan={16} />
                      <FormInput title="Last Name"  name="lastName"  titleSpan={8} wrapperSpan={16} />
                    </div>
                    <div className="ord-form-row">
                      <FormInput title="Phone" name="phoneNumber" titleSpan={8} wrapperSpan={16} />
                      <FormInput title="Email" name="email"       titleSpan={8} wrapperSpan={16} />
                    </div>
                  </>
                )}

                <FormInput title="Address" name="address" titleSpan={8} wrapperSpan={16} />
                <div className="ord-form-row">
                  <FormInput title="Pincode" name="postIndex" titleSpan={8} wrapperSpan={16} />
                  <div className="ord-delivery-badge-wrap">
                    {deliveryStatus === "AVAILABLE" && (
                      <span className="ord-delivery-badge available">✓ Delivery available</span>
                    )}
                    {deliveryStatus === "NOT_AVAILABLE" && (
                      <span className="ord-delivery-badge unavailable">✗ Not serviceable</span>
                    )}
                  </div>
                </div>
                <div className="ord-form-row">
                  <FormInput title="City"  name="city"  disabled titleSpan={8} wrapperSpan={16} />
                  <FormInput title="State" name="state" disabled titleSpan={8} wrapperSpan={16} />
                </div>
              </Form>
            </div>

            <div className="ord-card">
              <div className="ord-card-header">
                <CreditCardOutlined className="ord-card-icon" />
                <span className="ord-card-title">Payment Method</span>
              </div>
              <div className="ord-payment-options">
                <label className={`ord-payment-option ${paymentType === "COD" ? "selected" : ""}`}>
                  <Radio checked={paymentType === "COD"} onChange={() => setPaymentType("COD")} />
                  <div className="ord-payment-info">
                    <span className="ord-payment-name">Cash on Delivery</span>
                    <span className="ord-payment-sub">Pay when your order arrives</span>
                  </div>
                </label>
                <label className={`ord-payment-option ${paymentType === "ONLINE" ? "selected" : ""}`}>
                  <Radio checked={paymentType === "ONLINE"} onChange={() => setPaymentType("ONLINE")} />
                  <div className="ord-payment-info">
                    <span className="ord-payment-name">
                      <LockOutlined style={{ fontSize: 11, marginRight: 4 }} />
                      Online Payment
                    </span>
                    <span className="ord-payment-sub">UPI, Cards, Net Banking via Razorpay</span>
                  </div>
                </label>
              </div>
              {minOrderFail && (
                <div className="ord-min-order-warn">
                  Minimum order value is ₹{minOrderValue}
                  {isRetailer    ? " (Retailer)"    : ""}
                  {isDistributor ? " (Distributor)" : ""}
                </div>
              )}
            </div>
          </div>

          <div className="ord-right">
            <div className="ord-card ord-summary-card">
              <div className="ord-card-header">
                <ShoppingOutlined className="ord-card-icon" />
                <span className="ord-card-title">Order Summary</span>
                <span className="ord-item-count">
                  {perfumes.length} item{perfumes.length !== 1 ? "s" : ""}
                </span>
              </div>
              <div className="ord-items-list">
                {perfumes.map((p) => (
                  <OrderItem key={p.id} perfume={p} quantity={perfumesFromLocalStorage.get(p.id)} />
                ))}
              </div>
              <div className="ord-price-breakdown">
                <div className="ord-price-row"><span>Items total</span><span>₹{totalPrice}</span></div>
                <div className="ord-price-row"><span>Delivery</span><span className="ord-free">FREE</span></div>
                {minOrderValue > 0 && (
                  <div className="ord-price-row ord-min-note">
                    <span>Min. order</span><span>₹{minOrderValue}</span>
                  </div>
                )}
                <div className="ord-price-total"><span>Total Amount</span><span>₹{totalPrice}</span></div>
              </div>
              <button
                className="ord-confirm-btn"
                onClick={onFormSubmit}
                disabled={!isFormValid() || minOrderFail}
              >
                {paymentType === "ONLINE" ? `Proceed to Pay  ₹${totalPrice}` : "Place Order"}
              </button>
              <p className="ord-secure-note"><LockOutlined /> 100% Secure &amp; Encrypted</p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Order;